import { createContext, useContext, useMemo, type CSSProperties, type ReactNode } from 'react';

/**
 * The resolved theme, applied as CSS custom properties.
 *
 * ── THE SERVER RESOLVES; THIS APPLIES ────────────────────────────────────────────────
 *
 * Which theme wins — page, template, organization, platform — is decided by the engine,
 * because it is the only place that can see all four and the only place that can be
 * trusted to. What arrives here is a flat bundle of named tokens that has already been
 * validated twice: once when it was saved and once on the way out of the resolver.
 *
 * ── AND VALIDATES AGAIN, BECAUSE THIS IS THE LAST GATE ───────────────────────────────
 *
 * A token's value is about to be written into a style attribute. That is the moment where
 * `#fff; } body { display: none` or `url(javascript:...)` would matter, so it is checked
 * here too. The engine already refuses all of it; this costs one regex per token and
 * covers the case where a response came from somewhere the engine did not write — a
 * cached body, a proxy, a test harness, a future endpoint somebody adds without
 * remembering what the values are for.
 *
 * ── THE PROPERTY NAMES ARE A COMPILED ALLOW-LIST ─────────────────────────────────────
 *
 * §27. `colors.primary` becomes `--cms-color-primary` through the table below and through
 * nothing else. A token name is never transformed into a property name, so a token called
 * `x: red; --y` cannot become one either. Anything not in the table is dropped.
 *
 * ── NO THEME IS A NORMAL STATE ───────────────────────────────────────────────────────
 *
 * §28. With no theme, nothing is written and every block falls back to the token the
 * application already ships. That is why each CMS rule is written
 * `var(--cms-color-surface, var(--color-bg-surface))`: the themed value when there is
 * one, Voleto's own when there is not, and never an unstyled page.
 */

/** §27: token name -> the one custom property it may write. Nothing derives these. */
const TOKEN_TO_CSS_VAR: Readonly<Record<string, string>> = Object.freeze({
  'colors.background': '--cms-color-background',
  'colors.surface': '--cms-color-surface',
  'colors.text': '--cms-color-text',
  'colors.mutedText': '--cms-color-muted-text',
  'colors.primary': '--cms-color-primary',
  'colors.primaryText': '--cms-color-primary-text',
  'colors.border': '--cms-color-border',
  'colors.accent': '--cms-color-accent',

  'typography.headingFont': '--cms-font-heading',
  'typography.bodyFont': '--cms-font-body',
  'typography.headingWeight': '--cms-weight-heading',
  'typography.bodyWeight': '--cms-weight-body',

  'shape.radiusSmall': '--cms-radius-sm',
  'shape.radiusMedium': '--cms-radius-md',
  'shape.radiusLarge': '--cms-radius-lg',

  'layout.contentWidth': '--cms-width-content',
  'layout.wideWidth': '--cms-width-wide',
  'layout.sectionSpacing': '--cms-space-section',
});

/**
 * A font token names a face Voleto already ships; it never carries a family string.
 *
 * §24. The mapping lives here, in code, so a theme cannot name a font that is fetched
 * from somewhere or a family that resolves to whatever a viewer happens to have
 * installed. Three choices, all already loaded by the application.
 */
const FONT_STACKS: Readonly<Record<string, string>> = Object.freeze({
  display: 'var(--font-display)',
  body: 'var(--font-body)',
  mono: 'var(--font-mono)',
});

const HEX = /^#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
/** Lengths and weights: digits, a dot, and one of a short list of units. */
const SAFE_SCALAR = /^-?\d+(?:\.\d+)?(?:px|rem|em|ch|%)?$/;

export interface ResolvedTheme {
  /** Flat token bundle, e.g. { 'colors.primary': '#8B5CF6' }. */
  tokens: Record<string, string>;
  source: string;
}

const NO_THEME: ResolvedTheme = Object.freeze({
  tokens: Object.freeze({}) as Record<string, string>,
  source: 'platform_stylesheet',
});

const ThemeContext = createContext<ResolvedTheme>(NO_THEME);

export function useResolvedTheme(): ResolvedTheme {
  return useContext(ThemeContext);
}

/** The bundle as a style object. Exported so a test can assert it without rendering. */
export function themeToStyle(tokens: Record<string, string> | null | undefined): CSSProperties {
  const out: Record<string, string> = {};
  if (!tokens) return out as CSSProperties;

  for (const [token, cssVar] of Object.entries(TOKEN_TO_CSS_VAR)) {
    // Object.hasOwn: `tokens['constructor']` would resolve through the prototype chain
    // and hand us a function. Task 8 shipped exactly that bug in the copy layer.
    if (!Object.hasOwn(tokens, token)) continue;
    const raw = tokens[token];
    if (typeof raw !== 'string') continue;

    if (token.startsWith('colors.')) {
      if (!HEX.test(raw)) continue;
      out[cssVar] = raw;
      continue;
    }
    if (token === 'typography.headingFont' || token === 'typography.bodyFont') {
      if (!Object.hasOwn(FONT_STACKS, raw)) continue;
      out[cssVar] = FONT_STACKS[raw];
      continue;
    }
    if (!SAFE_SCALAR.test(raw)) continue;
    out[cssVar] = raw;
  }
  return out as CSSProperties;
}

export function CmsThemeProvider({
  theme, children,
}: { theme?: ResolvedTheme | null; children: ReactNode }) {
  const resolved = theme ?? NO_THEME;
  const style = useMemo(() => themeToStyle(resolved.tokens), [resolved.tokens]);

  return (
    <ThemeContext.Provider value={resolved}>
      {Object.keys(style).length > 0
        ? <div className="cms-theme" data-theme-source={resolved.source} style={style}>{children}</div>
        : children}
    </ThemeContext.Provider>
  );
}
